import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchDashboard } from '../api/dashboard';

const STATUS_META = {
  validated: { label: 'Validado agora', color: '#7ddea3' },
  already: { label: 'Já validado', color: '#f0c57a' },
  invalid: { label: 'Inválido', color: '#f0a39d' },
};

const Page = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  padding: theme.spacing(4, 2, 6),
  color: '#f4f1ea',
}));

const Card = styled(Paper)({
  background: '#141414',
  border: '1px solid #2a241c',
  borderRadius: 18,
  boxShadow: 'none',
  boxSizing: 'border-box',
  minWidth: 0,
  overflow: 'hidden',
});

const KpiCard = styled(Card)({
  padding: '18px 18px 16px',
});

const SectionCard = styled(Card)({
  padding: '20px 20px 16px',
});

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: '#1c1c1c',
        border: '1px solid #3a342c',
        borderRadius: 2,
        px: 1.5,
        py: 1,
      }}
    >
      <Typography sx={{ color: '#e8c089', fontSize: '0.75rem', mb: 0.5 }}>{label}</Typography>
      {payload.map((item) => (
        <Typography key={item.dataKey} sx={{ color: item.color, fontSize: '0.8rem' }}>
          {item.name}: {item.value}
        </Typography>
      ))}
    </Box>
  );
};

const percent = (part, total) => {
  if (!total) return '0%';
  return `${Math.round((part / total) * 100)}%`;
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchDashboard()
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch(() => {
        if (!cancelled) setError('Não foi possível carregar o painel.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Page maxWidth="lg">
        <Typography sx={{ color: '#f0a39d' }}>{error}</Typography>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={26} sx={{ color: '#c47a2e' }} />
      </Page>
    );
  }

  const { kpis } = data;
  const statusShare = [
    { key: 'validated', name: 'Validados', value: kpis.validated, color: '#2ea05a' },
    { key: 'already', name: 'Já validados', value: kpis.alreadyValidated, color: '#c47a2e' },
    { key: 'invalid', name: 'Inválidos', value: kpis.invalid, color: '#dc463c' },
  ];
  const kpiItems = [
    { label: 'Leituras hoje', value: kpis.reads, hint: `${kpis.uniqueInvites} convites únicos` },
    { label: 'Validados agora', value: kpis.validated, hint: percent(kpis.validated, kpis.reads) },
    { label: 'Já validados', value: kpis.alreadyValidated, hint: 'Tentativa repetida na porta' },
    { label: 'Inválidos', value: kpis.invalid, hint: 'Inexistente ou não encontrado' },
    { label: 'Com alerta', value: kpis.alerts, hint: 'Cortesia, imprensa, acesso especial' },
    { label: 'Taxa de entrada', value: percent(kpis.validated, kpis.reads), hint: 'Validados / leituras' },
  ];

  return (
    <Page maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          flexWrap: 'wrap',
          gap: 2,
          mb: 3.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#c47a2e',
              letterSpacing: '0.24em',
              fontWeight: 600,
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              mb: 0.75,
            }}
          >
            Painel operacional
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', sm: '2.2rem' }, letterSpacing: '-0.03em' }}>
            Boi Fest {data.eventYear}
          </Typography>
          <Typography sx={{ color: '#9a9186', mt: 0.5 }}>{data.periodLabel}</Typography>
        </Box>
        <Box sx={{ textAlign: { sm: 'right' } }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{ color: '#c47a2e', textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem' }}
          >
            ← Voltar ao leitor
          </Typography>
          {data.source === 'mock' && (
            <Typography sx={{ color: '#7a736a', fontSize: '0.78rem', mt: 0.75 }}>
              Dados de exemplo · endpoint PHP depois
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          columnGap: 2,
          rowGap: 2,
          mb: 3,
        }}
      >
        {kpiItems.map((item) => (
          <KpiCard key={item.label}>
            <Typography sx={{ color: '#9a9186', fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {item.label}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.85rem', mt: 0.75, lineHeight: 1.1, color: '#f4f1ea' }}>
              {item.value}
            </Typography>
            <Typography sx={{ color: '#7a736a', fontSize: '0.78rem', mt: 0.75 }}>{item.hint}</Typography>
          </KpiCard>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' },
          columnGap: 2,
          rowGap: 2,
          mb: 3,
        }}
      >
        <SectionCard>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>Leituras por hora</Typography>
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.hourly} barGap={2}>
                <CartesianGrid stroke="#2a241c" vertical={false} />
                <XAxis dataKey="hour" stroke="#7a736a" tick={{ fill: '#9a9186', fontSize: 12 }} axisLine={false} />
                <YAxis stroke="#7a736a" tick={{ fill: '#9a9186', fontSize: 12 }} axisLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(196, 122, 46, 0.08)' }} />
                <Bar dataKey="validated" name="Validados" stackId="reads" fill="#2ea05a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="alreadyValidated" name="Já validados" stackId="reads" fill="#c47a2e" />
                <Bar dataKey="invalid" name="Inválidos" stackId="reads" fill="#dc463c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </SectionCard>

        <SectionCard>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>Composição das leituras</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {statusShare.map((item) => (
              <Box key={item.key}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                  <Typography sx={{ color: '#d8d0c6', fontSize: '0.9rem' }}>{item.name}</Typography>
                  <Typography sx={{ color: item.color, fontWeight: 700 }}>
                    {item.value} · {percent(item.value, kpis.reads)}
                  </Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: 99, background: '#1c1c1c', overflow: 'hidden' }}>
                  <Box sx={{ width: percent(item.value, kpis.reads), height: '100%', background: item.color }} />
                </Box>
              </Box>
            ))}
          </Box>
        </SectionCard>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
          columnGap: 2,
          rowGap: 2,
          mb: 3,
        }}
      >
        <SectionCard>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Últimas leituras</Typography>
          {data.recent.map((item) => {
            const meta = STATUS_META[item.status] || STATUS_META.invalid;
            return (
              <Box
                key={`${item.at}-${item.code}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  py: 1.1,
                  borderBottom: '1px solid #2a241c',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: meta.color }}>
                    {item.number ? `${item.number} · ${item.code}` : item.code}
                  </Typography>
                  <Typography sx={{ color: '#9a9186', fontSize: '0.8rem' }}>
                    {item.msg}
                    {item.alerta ? ` · ${item.alerta}` : ''}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#7a736a', fontSize: '0.78rem', flexShrink: 0 }}>{item.at}</Typography>
              </Box>
            );
          })}
        </SectionCard>

        <SectionCard>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Alertas na porta</Typography>
          {data.alerts.map((item) => (
            <Box
              key={`${item.at}-${item.code}`}
              sx={{
                py: 1.1,
                borderBottom: '1px solid #2a241c',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Typography sx={{ fontWeight: 700, color: '#f0c57a' }}>{item.alerta}</Typography>
              <Typography sx={{ color: '#9a9186', fontSize: '0.8rem' }}>
                {item.number} · {item.code} · {item.at}
              </Typography>
            </Box>
          ))}
        </SectionCard>
      </Box>

      <SectionCard>
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Convites mais relidos</Typography>
        <Typography sx={{ color: '#7a736a', fontSize: '0.85rem', mb: 1.5 }}>
          Mesmo QR passado mais de uma vez — costuma ser fila, foto ou convite compartilhado.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 1.5,
            rowGap: 1.5,
          }}
        >
          {data.topRepeats.map((item) => (
            <Box
              key={item.code}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 1.1,
                borderRadius: 2,
                background: '#1c1c1c',
                border: '1px solid #2a241c',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                {item.number} · {item.code}
              </Typography>
              <Typography sx={{ color: '#f0c57a', fontWeight: 700 }}>{item.count}x</Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>
    </Page>
  );
};

export default Dashboard;
